import { useCallback, useSyncExternalStore } from 'react';

type Location = {
  latitude: number;
  longitude: number;
  accuracy: number;
};

type State = {
  permission: PermissionState;
  location?: Location;
  error?: GeolocationPositionError | Error;
};

let state: State = {
  permission: 'prompt',
};

export function useGeolocation() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const requestLocation = useCallback(() => {
    resolveLocation();
  }, []);

  return {
    location: state.location,
    permission: state.permission,
    error: state.error,
    requestLocation,
    resetLocation,
  };
}

const listeners = new Set<() => void>();

let permissionStatus: PermissionStatus | undefined;
let locationRequestId = 0;
let permissionQueryId = 0;

function getSnapshot() {
  return state;
}

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  const currentPermissionQueryId = permissionQueryId;

  // Check current permission state when subscribing.
  navigator.permissions?.query({ name: 'geolocation' }).then((status) => {
    if (currentPermissionQueryId !== permissionQueryId) {
      return;
    }

    permissionStatus = status;

    state = {
      ...state,
      permission: status.state,
    };

    emit();

    status.addEventListener('change', handlePermissionChange);

    if (status.state === 'granted') {
      resolveLocation();
    }
  });

  return () => {
    listeners.delete(listener);

    permissionStatus?.removeEventListener('change', handlePermissionChange);
  };
}

function handlePermissionChange() {
  if (!permissionStatus) {
    return;
  }

  state = {
    ...state,
    permission: permissionStatus.state,
    // Clear location when permission is revoked to respect user intent.
    location: permissionStatus.state === 'granted' ? state.location : undefined,
  };

  emit();

  // Permission may have been granted outside this hook.
  if (permissionStatus.state === 'granted') {
    resolveLocation();
  }
}

function resolveLocation() {
  const currentLocationRequestId = ++locationRequestId;

  if (!navigator.geolocation) {
    state = {
      ...state,
      error: new Error('Geolocation is not supported'),
    };

    emit();
    return;
  }

  navigator.geolocation.getCurrentPosition(
    ({ coords }) => {
      if (currentLocationRequestId !== locationRequestId) {
        return;
      }

      state = {
        ...state,
        location: {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
        },
        error: undefined,
      };

      emit();
    },
    (error) => {
      if (currentLocationRequestId !== locationRequestId) {
        return;
      }

      state = {
        ...state,
        error,
      };

      emit();
    },
  );
}

function resetLocation() {
  locationRequestId += 1;
  permissionQueryId += 1;

  state = {
    ...state,
    location: undefined,
    error: undefined,
  };

  emit();
}
